<template lang="pug">
  yz-dialog(v-if="showDialog" v-model="showDialog" fullscreen persistent scrollable)
    yz-card
      yz-dialog-fullscreen-header(title="Bulk update panel claim status" :loading="isLoading" @back="onCloseDialog")
        template(#actions)
          .d-flex.align-center(style="gap: 12px")
            yz-btn(size="lg" text label="Cancel" @click="onCancelDialog")
            yz-tooltip(v-if="!canSave" :text="saveButtonTooltip" direction="left")
              yz-btn(size="lg" label="Save" :disabled="!canSave" :loading="isLoading" @click="onSaveDialog")
            yz-btn(v-else size="lg" label="Save" :loading="isLoading" @click="onSaveDialog")
      yz-dialog-fullscreen-content.pa-6
        // Header Summary Section
        yz-card.pa-4.mb-6(color="yz-neutral-300")
          v-row(no-gutters align="center")
            v-col(cols="auto")
              .d-flex.align-center
                .yz-medium-body-sm.text-neutral-700 {{ displayPanelName }}
                yz-divider.mx-2(vertical, color="yz-neutral-400")
                .yz-medium-body-xs.text-neutral-500 {{ selectedItems.length }} panel claim{{ selectedItems.length > 1 ? 's' : '' }}
              .yz-heading-6.text-neutral-700.mt-2 {{ totalSubmittedAmount | displayPrice($currency) }}
            v-spacer
            v-col(cols="auto")
              .d-flex.align-center
                yz-btn.mr-3(size="lg" outlined background-color="white" label="Set received date for all panel claims" @click="onSetAllReceivedDate")
                yz-btn(size="lg" outlined background-color="white" label="Set admin fee for all panel claims" @click="onSetAllAdminFee")
                yz-divider.mx-4(vertical color="yz-neutral-400")
                .text-right
                  .yz-medium-body-sm.text-neutral-700.mb-1 Outstanding amount
                  .yz-heading-6.text-success-500 {{ outstandingAmount | displayPrice($currency) }}
        // Claims Table
        yz-card(outlined)
          .pa-4
            yz-data-table(:headers="tableHeaders" :items="claimsData" :items-per-page="pageSize" :page="currentPage" disable-sort)
              template(v-slot:item.amount="{ item }")
                .yz-medium-body-sm {{ item.amount }}
              template(v-slot:item.claimNo="{ item }")
                .yz-medium-body-sm {{ item.claimNo }}
              template(v-slot:item.panel="{ item }")
                yz-badge(border size="sm") {{ item.panel }}
              template(v-slot:item.status="{ item }")
                claim-status-badge(status="received" size="sm")
              template(v-slot:item.receivedDate="{ item }")
                yz-text-field.my-2(:value="formatDate(item.receivedDate)" placeholder="Select received date" prepend-inner-icon="$yzi-calendar" readonly clickable clearable size="md" required :rules="dateRules" required-error-message="Date is required" @click="openDatePicker(item)" @click:clear="item.receivedDate = null")
              template(v-slot:item.receivedAmount="{ item }")
                yz-text-field.my-2(v-model="item.receivedAmountDisplay" type="text" size="md" readonly, background-color="yz-neutral-200")
              template(v-slot:item.adminFee="{ item }")
                .d-flex.align-center.gap-2.my-2
                  yz-dropdown(v-model="item.adminFee" :items="adminFeeOptions" style="max-width: 150px" @input="onAdminFeeTypeChange(item)")
                  yz-text-field(v-model.number="item.adminFeeValue" type="number" size="md" :prefix="item.adminFee === 'fixed' ? $currency.symbol : ''" :suffix="item.adminFee === 'percentage' ? '%' : ''" required required-error-message="Admin fee is required" :rules="adminFeeRules(item)" min="0" @input="onAdminFeeValueChange(item)")
              template(v-slot:item.rejectedAmount="{ item }")
                yz-text-field.my-2(v-model.number="item.rejectedAmountInput" type="number" size="md" :prefix="$currency.symbol" required, required-error-message="Rejected amount is required" :rules="rejectedAmountRules(item)" min="0" @input="onRejectedAmountChange(item)")
              template(v-slot:item.actions="{ item }")
                yz-btn(v-if="item.rejectedAmount > 0" :label="item.balanceAssigned ? 'Assigned' : 'Assign balance'" :append-icon="item.balanceAssigned ? '' : '$yzi-switch-horizontal-duotone-01'" size="sm" icon-color="yz-neutral-500" outlined :disabled="item.balanceAssigned" @click="onAssignBalance(item)")
            // Pagination
            .d-flex.justify-end.mt-4
              yz-pagination(v-model="currentPage" :page-size="pageSize" :count="totalCount" type="item" arrow)

    // Set Received Date Dialog
    set-date-dialog(v-if="showSetReceivedDateDialog" v-model="showSetReceivedDateDialog" status="received" @confirmed="onReceivedDateConfirmed" @canceled="showSetReceivedDateDialog = false")

    // Set Admin Fee Dialog
    set-admin-fee-dialog(v-if="showSetAdminFeeDialog" v-model="showSetAdminFeeDialog" @confirmed="onAdminFeeConfirmed" @canceled="showSetAdminFeeDialog = false")

    // Bulk Assign Balance Dialog
    bulk-assign-balance-dialog(v-if="showBulkAssignBalanceDialog" v-model="showBulkAssignBalanceDialog" :parent-claim="currentBulkClaimForAssignment" :rejected-amount="currentBulkClaimForAssignment.rejectedAmount" @confirmed="onBulkAssignBalanceConfirmed" @cancel="onBulkAssignBalanceCanceled")

    // Individual Date Picker Dialog
    yz-dialog(v-if="currentEditingItem" v-model="showDatePickerDialog" max-width="380px")
      yz-card
        yz-dialog-normal-content.px-2.pt-2.pb-0
          yz-date-picker(v-model="currentEditingItem.receivedDate" scrollable, @change="showDatePickerDialog = false")

    // Bulk Change Status Confirmation Dialog
    bulk-change-claim-status-confirmation-dialog(v-if="showChangeStatusDialog" v-model="showChangeStatusDialog" current-status="submitted" new-status="received" @confirmed="handleBulkUpdate" @canceled="showChangeStatusDialog = false")
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { DateTime } from 'luxon'
import { validateDecimalPlaces } from '@/utils'
import ClaimStatusBadge from '@/components/clinic/panel/claims/ClaimStatusBadge.vue'
import SetDateDialog from './SetDateDialog.vue'
import SetAdminFeeDialog from './SetAdminFeeDialog.vue'
import BulkAssignBalanceDialog from '@/components/clinic/panel/dialogs/bulk/BulkAssignBalanceDialog.vue'
import BulkChangeClaimStatusConfirmationDialog from '@/components/clinic/panel/dialogs/BulkChangeClaimStatusConfirmationDialog.vue'

export default {
  components: { ClaimStatusBadge, SetDateDialog, SetAdminFeeDialog, BulkAssignBalanceDialog, BulkChangeClaimStatusConfirmationDialog },
  props: {
    value: {
      type: Boolean,
      default: false
    },
    selectedItems: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      showSetReceivedDateDialog: false,
      showSetAdminFeeDialog: false,
      showBulkAssignBalanceDialog: false,
      showDatePickerDialog: false,
      showChangeStatusDialog: false,
      currentBulkClaimForAssignment: null,
      currentEditingItem: null,
      currentPage: 1,
      pageSize: 10,
      isLoading: false,
      tableHeaders: [
        { text: 'Amount', value: 'amount' },
        { text: 'Claim No', value: 'claimNo' },
        { text: 'Claim Type', value: 'type', cellClass: 'capitalize' },
        { text: 'Panel', value: 'panel' },
        { text: 'Status', value: 'status' },
        { text: 'Received Date', value: 'receivedDate' },
        { text: 'Received Amount', value: 'receivedAmount' },
        { text: 'Admin Fee', value: 'adminFee' },
        { text: 'Rejected Amount', value: 'rejectedAmount' },
        { text: 'Assign balance', value: 'actions', sortable: false, align: 'end', width: '200px' }
      ],
      claimsData: [],
      adminFeeOptions: [
        { text: 'Fixed', value: 'fixed' },
        { text: 'Percentage', value: 'percentage' }
      ]
    }
  },
  mounted() {
    this.initializeClaimsData()
  },
  computed: {
    ...mapGetters({
      company: 'getCompany'
    }),
    showDialog: {
      get() {
        return this.value
      },
      set(newValue) {
        this.$emit('input', newValue)
      }
    },
    totalCount() {
      return this.claimsData.length
    },
    panelBreakdown() {
      const breakdown = {}
      this.selectedItems.forEach(claim => {
        const panelName = claim.panel?.name || 'N/A'
        if (!breakdown[panelName]) {
          breakdown[panelName] = {
            name: panelName,
            count: 0
          }
        }
        breakdown[panelName].count++
      })
      return Object.values(breakdown)
    },
    primaryPanel() {
      if (this.panelBreakdown.length === 1) {
        const panel = this.panelBreakdown[0]
        return panel.name !== 'N/A' ? panel.name : null
      }
      return null
    },
    displayPanelName() {
      return this.primaryPanel || 'Multiple Panels'
    },
    totalSubmittedAmount() {
      return this.selectedItems.reduce((sum, claim) => sum + (claim.submitted_amount || 0), 0)
    },
    outstandingAmount() {
      // Sum of unassigned rejected amounts for bulk claims
      return this.claimsData.reduce((sum, claim) => {
        if (claim.type === 'bulk' && claim.rejectedAmount > 0 && !claim.balanceAssigned) {
          return sum + claim.rejectedAmount
        }
        return sum
      }, 0)
    },
    canSave() {
      // Check if all required fields are filled and valid
      const hasInvalidDates = this.claimsData.some(claim => !claim.receivedDate)
      const hasInvalidAdminFee = this.claimsData.some(claim => {
        const value = claim.adminFeeValue
        const rules = this.adminFeeRules(claim)
        return !rules.every(rule => rule(value) === true)
      })
      const hasInvalidRejectedAmount = this.claimsData.some(claim => {
        const value = claim.rejectedAmountInput
        const rules = this.rejectedAmountRules(claim)
        return !rules.every(rule => rule(value) === true)
      })
      const hasUnassignedBalance = this.claimsData.some(claim => claim.type === 'bulk' && claim.rejectedAmount > 0 && !claim.balanceAssigned)

      return !hasInvalidDates && !hasInvalidAdminFee && !hasInvalidRejectedAmount && !hasUnassignedBalance
    },
    saveButtonTooltip() {
      const missingDates = this.claimsData.filter(claim => !claim.receivedDate).length
      const invalidAdminFee = this.claimsData.filter(claim => {
        const value = claim.adminFeeValue
        const rules = this.adminFeeRules(claim)
        return !rules.every(rule => rule(value) === true)
      }).length
      const invalidRejectedAmount = this.claimsData.filter(claim => {
        const value = claim.rejectedAmountInput
        const rules = this.rejectedAmountRules(claim)
        return !rules.every(rule => rule(value) === true)
      }).length
      const unassignedBalance = this.claimsData.filter(claim => claim.type === 'bulk' && claim.rejectedAmount > 0 && !claim.balanceAssigned).length

      const issues = []
      if (missingDates > 0) {
        issues.push(`set received dates for ${missingDates} claim(s)`)
      }
      if (invalidAdminFee > 0) {
        issues.push(`fix invalid admin fee for ${invalidAdminFee} claim(s)`)
      }
      if (invalidRejectedAmount > 0) {
        issues.push(`fix invalid rejected amount for ${invalidRejectedAmount} claim(s)`)
      }
      if (unassignedBalance > 0) {
        issues.push(`assign balance for ${unassignedBalance} bulk claim(s)`)
      }

      if (issues.length === 0) return ''
      return `Please ${issues.join(', ')}`
    },
    dateRules() {
      return [
        v => !!v || 'Date is required'
      ]
    },
    adminFeeRules() {
      return (claim) => {
        if (claim.adminFee === 'percentage') {
          // Percentage rules
          return [
            v => (v !== null && v !== '') || 'Admin fee is required',
            v => v === null || v === '' || (v >= 0 && v <= 100) || 'Percentage must be between 0 and 100',
            v => v === null || v === '' || Number.isInteger(Number(v)) || 'Percentage must be integer'
          ]
        } else {
          // Fixed amount rules
          return [
            v => (v !== null && v !== '') || 'Admin fee is required',
            v => v === null || v === '' || v >= 0 || 'Admin fee cannot be negative',
            v => validateDecimalPlaces(v, this.$currency)
          ]
        }
      }
    },
    rejectedAmountRules() {
      return (claim) => {
        const submittedAmountInCurrency = claim.submittedAmount / this.$currency.multiplier
        return [
          v => (v !== null && v !== '') || 'Rejected amount is required',
          v => v === null || v === '' || v >= 0 || 'Rejected amount cannot be negative',
          v => v === null || v === '' || v <= submittedAmountInCurrency || `Rejected amount cannot exceed ${this.$options.filters.displayPrice(claim.submittedAmount, this.$currency)}`,
          v => validateDecimalPlaces(v, this.$currency)
        ]
      }
    }
  },
  methods: {
    ...mapActions(['showMessage']),
    formatDate(dateString) {
      if (!dateString) return ''
      return DateTime.fromISO(dateString).toFormat('dd/MM/yyyy')
    },
    openDatePicker(item) {
      this.currentEditingItem = item
      this.showDatePickerDialog = true
    },
    initializeClaimsData() {
      this.claimsData = this.selectedItems.map(claim => {
        const initialAmount = claim.submitted_amount || 0
        return {
          // Original claim data
          id: claim.id,
          type: claim.type,
          submittedAmount: initialAmount,
          invoices: claim.invoices || [],

          // Display data
          amount: this.$options.filters.displayPrice(initialAmount, this.$currency),
          claimNo: claim.rid || '',
          panel: claim.panel?.name || 'N/A',
          status: claim.status,

          // Editable fields
          receivedDate: null,
          adminFee: 'fixed',
          adminFeeValue: 0,
          adminFeePercentage: null,
          rejectedAmountInput: 0, // Display value in currency format
          rejectedAmount: 0, // Internal value in cents

          // Computed fields
          receivedAmount: initialAmount,
          receivedAmountDisplay: this.$options.filters.displayPrice(initialAmount, this.$currency),

          // Balance assignment tracking
          balanceAssigned: false,
          assignedInvoices: []
        }
      })
    },
    calculateReceivedAmount(claim) {
      const submittedAmount = claim.submittedAmount || 0
      const rejectedAmount = parseFloat(claim.rejectedAmount) || 0

      let adminFeeInCents = 0
      if (claim.adminFeeValue) {
        if (claim.adminFee === 'percentage') {
          // For percentage: calculate admin fee in cents from percentage
          const baseAmount = submittedAmount - rejectedAmount
          const percentage = parseFloat(claim.adminFeeValue) || 0
          adminFeeInCents = Math.round((baseAmount * percentage) / 100)
          claim.adminFeePercentage = percentage
        } else {
          // For fixed: convert display value to cents
          adminFeeInCents = Math.round(parseFloat(claim.adminFeeValue) * this.$currency.multiplier)
          claim.adminFeePercentage = null
        }
      }

      // Store the admin fee in cents for API submission
      claim.adminFeeInCents = adminFeeInCents

      // Calculate raw amount for API (all values in cents)
      const rawAmount = submittedAmount - adminFeeInCents - rejectedAmount
      claim.receivedAmount = rawAmount

      // Format for display
      claim.receivedAmountDisplay = this.$options.filters.displayPrice(rawAmount, this.$currency)
    },
    onRejectedAmountChange(claim) {
      // Convert display value (currency format) to cents
      const inputValue = parseFloat(claim.rejectedAmountInput) || 0
      claim.rejectedAmount = Math.round(inputValue * this.$currency.multiplier)

      // Auto-assign balance for single claims with rejected amount
      if (claim.type === 'single' && claim.rejectedAmount > 0) {
        claim.balanceAssigned = true
        const balanceAmount = claim.submittedAmount - claim.rejectedAmount
        claim.assignedInvoices = [{
          uid: claim.invoices[0],
          amount: balanceAmount,
          rejected_amount: claim.rejectedAmount
        }]
      } else if (claim.type === 'single' && claim.rejectedAmount === 0) {
        // Clear assignment when rejected amount is set to 0
        claim.balanceAssigned = false
        claim.assignedInvoices = []
      }

      // Recalculate received amount
      this.calculateReceivedAmount(claim)
    },
    onAdminFeeValueChange(claim) {
      // Recalculate received amount when admin fee value changes
      this.calculateReceivedAmount(claim)
    },
    onAdminFeeTypeChange(claim) {
      // Recalculate received amount when fee type changes (fixed/percentage)
      this.calculateReceivedAmount(claim)
    },
    onCloseDialog() {
      this.$emit('input', false)
      this.$emit('close')
    },
    onCancelDialog() {
      this.$emit('input', false)
      this.$emit('canceled')
    },
    async onSaveDialog() {
      if (!this.canSave) {
        return
      }

      // Show confirmation dialog
      this.showChangeStatusDialog = true
    },
    async handleBulkUpdate(logNote) {
      this.isLoading = true
      try {
        // Build the claims payload
        const claims = this.claimsData.map(claim => {
          const payload = {
            id: claim.id,
            status: 'received',
            received_amount: claim.receivedAmount,
            received_date: claim.receivedDate,
            rejected_amount: claim.rejectedAmount || 0,
            admin_fee: claim.adminFeeInCents || 0,
            admin_fee_percentage: claim.adminFeePercentage || null
          }

          // Add invoices for balance assignment (already populated in assignedInvoices)
          if (claim.balanceAssigned && claim.assignedInvoices.length > 0) {
            payload.invoices = claim.assignedInvoices
          }

          return payload
        })

        // Call bulk update API with log_note
        await this.$axios.post(`/panels/${this.company.slug}/claims/bulk_update`, { claims, log_note: logNote || null })

        await this.showMessage(['success', `${this.claimsData.length} panel claim${this.claimsData.length > 1 ? 's' : ''} updated successfully`])

        this.$emit('confirmed', this.claimsData)
        this.$emit('input', false)
      } catch (error) {
        console.error('Error updating claims:', error)
        await this.showMessage(['error', 'Failed to update claims. Please try again.'])
      } finally {
        this.isLoading = false
        this.showChangeStatusDialog = false
      }
    },
    onSetAllReceivedDate() {
      this.showSetReceivedDateDialog = true
    },
    onReceivedDateConfirmed(date) {
      // Set the received date for all claims
      this.claimsData.forEach(claim => {
        claim.receivedDate = date
      })
      this.showSetReceivedDateDialog = false
    },
    onSetAllAdminFee() {
      this.showSetAdminFeeDialog = true
    },
    onAdminFeeConfirmed({ feeType, feeValue }) {
      // Set the admin fee for all claims
      this.claimsData.forEach(claim => {
        claim.adminFee = feeType
        claim.adminFeeValue = feeValue
        // Recalculate received amount after setting admin fee
        this.calculateReceivedAmount(claim)
      })
      this.showSetAdminFeeDialog = false
    },
    onAssignBalance(claim) {
      // Find the original claim from selectedItems to get all required data
      const originalClaim = this.selectedItems.find(item => item.id === claim.id)
      if (!originalClaim) {
        console.error('Original claim not found')
        return
      }

      // Create a parent claim object that matches the structure expected by BulkAssignBalanceDialog
      this.currentBulkClaimForAssignment = {
        ...claim,
        panel: originalClaim.panel,
        rid: originalClaim.rid,
        invoices: originalClaim.invoices,
        received_date: claim.receivedDate
      }
      this.showBulkAssignBalanceDialog = true
    },
    onBulkAssignBalanceConfirmed(assignBalanceData) {
      if (assignBalanceData && assignBalanceData.invoices && this.currentBulkClaimForAssignment) {
        // Find the claim in claimsData and update it
        const claimIndex = this.claimsData.findIndex(c => c.id === this.currentBulkClaimForAssignment.id)
        if (claimIndex !== -1) {
          this.claimsData[claimIndex].balanceAssigned = true
          this.claimsData[claimIndex].assignedInvoices = assignBalanceData.invoices
          this.claimsData[claimIndex].rejectedAmount = assignBalanceData.totalRejectedAmount
          this.claimsData[claimIndex].rejectedAmountInput = assignBalanceData.totalRejectedAmount / this.$currency.multiplier
        }
      }
      this.showBulkAssignBalanceDialog = false
      this.currentBulkClaimForAssignment = null
    },
    onBulkAssignBalanceCanceled() {
      this.showBulkAssignBalanceDialog = false
      this.currentBulkClaimForAssignment = null
    }
  }
}
</script>

<style lang="scss" scoped></style>
